/**
 * Streaming TTS Service
 *
 * Orchestrates the lifecycle of a streaming text-to-speech session.
 * Fed tokens from the conversation stream (Socket.IO 'token' events),
 * forwards them to the selected TTS provider for progressive audio synthesis.
 *
 * Two session types:
 * - Server: POST /api/speech/tts per sentence, played via expo-audio (the
 *   default 'azure-server' provider — synthesis happens backend-side)
 * - Device: Uses SentenceChunker + expo-speech utterance queuing
 *
 * This service is stateful — create one per streaming session, dispose after.
 *
 * ## UI Behavior (stop/speaker button)
 *
 * TTSButtonGuard in MessageBubble controls visibility based solely on
 * streaming TTS status and whether messageText is available. It does NOT
 * depend on message.status ('streaming'/'complete') — this decouples the
 * button from the backend 'done' event, which may be delayed by analysis
 * in parallel mode.
 *
 * - Streaming TTS active (starting/active/finishing) → stop button (⏹)
 * - messageText available, no active streaming TTS → speaker button (🔊)
 * - No messageText → hidden
 *
 * Manual replay always uses one-shot ttsStore.speak(), never the streaming path.
 *
 * ### TTS available (Server or Device)
 *
 *   Phase                  | TTS status          | Button
 *   -----------------------|---------------------|------------------
 *   Streaming + auto-play  | starting / active   | Stop (⏹)
 *   User cancels mid-stream| canceled            | Speaker (🔊)
 *   Text done, audio plays | finishing           | Stop (⏹)
 *   Everything done        | done                | Speaker (🔊)
 *   No auto-play, has text | —                   | Speaker (🔊)
 *   Manual tap on speaker  | —                   | Stop/Speaker toggle
 *
 * ### No device TTS — voices check fails (getAvailableVoicesAsync returns [])
 *
 *   Phase                  | TTS status          | Button
 *   -----------------------|---------------------|------------------
 *   Auto-play starts       | error               | Speaker (🔊)
 *   Tokens arrive          | error               | Speaker (🔊)
 *   Text done              | error               | Speaker (🔊)
 *
 *   No auto-play fallback fires — 'error' is in the streamingTTSHandled set.
 *   User can tap speaker icon manually → ttsStore.speak() → speakWithDevice()
 *   which also fails → error badge shown on the speaker button.
 *
 * ### No device TTS — voices reported but engine broken (emulator)
 *
 *   Phase                  | TTS status          | Button
 *   -----------------------|---------------------|------------------
 *   Auto-play starts       | active              | Stop (⏹)
 *   First utterance fails  | error               | Speaker (🔊)
 *   Tokens arrive          | error               | Speaker (🔊)
 *   Text done              | error               | Speaker (🔊)
 *
 *   Voice check passes but Speech.speak() fires onError on first utterance
 *   (or no callback at all — 5s utterance timeout triggers abort).
 *   Session aborts immediately (fail-fast). If any utterance has previously
 *   succeeded, subsequent failures are tolerated (transient audio focus loss).
 *   No auto-play fallback fires. Manual speaker tap → error badge + 5s timeout.
 */

import { Platform } from 'react-native';
import * as Speech from 'expo-speech';
import { SentenceChunker } from '../utils/sentenceChunker';
import { TtsSegmenter } from '../utils/ttsSegmenter';
import {
  configureTtsAudioMode,
  fetchTtsAudio,
  playTtsClip,
  type TtsClip,
  type TtsPlayback,
} from './serverTtsPlayer';
import { createLogger } from '../utils/logger';
import { TTS_SESSION_WATCHDOG_MS } from '../config/timeouts';

const log = createLogger('StreamingTTS');
const DEVICE_TTS_UTTERANCE_TIMEOUT_MS = 5_000;

export type StreamingTTSStatus = 'idle' | 'starting' | 'active' | 'finishing' | 'done' | 'error' | 'canceled';

export interface StreamingTTSCallbacks {
  onStatusChange: (status: StreamingTTSStatus) => void;
  onError: (error: Error) => void;
  onComplete: () => void;
  /**
   * Fired once, when the session's first audio becomes audible — the
   * TTS_TTFA instrument (see server-autoplay-tts.mobile.md ATTS-005).
   * Fired by the device session (expo-speech onStart) and the server
   * session (first clip playback).
   */
  onFirstAudio?: () => void;
}

/**
 * Create a device TTS streaming session using expo-speech.
 * Uses SentenceChunker to accumulate tokens and speaks each sentence
 * via the OS TTS engine with utterance queuing.
 *
 * No Azure credentials needed — works entirely on-device.
 */
export function createDeviceStreamingTTSSession(callbacks: StreamingTTSCallbacks, language: string) {
  let status: StreamingTTSStatus = 'idle';
  let disposed = false;
  const chunker = new SentenceChunker();
  let speakQueue: Promise<void>[] = [];
  // If the first utterance fails, the engine is broken — abort the session
  let hasSucceeded = false;
  let firstAudioFired = false;

  function setStatus(newStatus: StreamingTTSStatus) {
    if (disposed && newStatus !== 'canceled') return;
    status = newStatus;
    callbacks.onStatusChange(newStatus);
  }

  function abortSession(reason: string) {
    if (disposed || status === 'error') return;
    disposed = true;
    chunker.reset();
    try { Speech.stop(); } catch { /* no-op */ }
    setStatus('error');
    callbacks.onError(new Error(reason));
  }

  function speakSentence(sentence: string): void {
    if (disposed) return;
    const promise = new Promise<void>((resolve) => {
      const timeout = setTimeout(() => {
        log.warn('Device TTS utterance timed out', { sentence: sentence.slice(0, 50) });
        if (!hasSucceeded) {
          abortSession('Device TTS engine not responding');
        }
        resolve();
      }, DEVICE_TTS_UTTERANCE_TIMEOUT_MS);

      Speech.speak(sentence, {
        language,
        rate: 0.9,
        onStart: () => {
          if (!firstAudioFired && !disposed) {
            firstAudioFired = true;
            callbacks.onFirstAudio?.();
          }
        },
        onDone: () => { clearTimeout(timeout); hasSucceeded = true; resolve(); },
        onStopped: () => { clearTimeout(timeout); resolve(); },
        onError: () => {
          clearTimeout(timeout);
          log.warn('Device TTS utterance failed', { sentence: sentence.slice(0, 50) });
          if (!hasSucceeded) {
            abortSession('Device TTS engine not available');
          }
          resolve();
        },
      });
    });
    speakQueue.push(promise);
  }

  return {
    get status() {
      return status;
    },

    get isActive() {
      return status === 'active' || status === 'starting' || status === 'finishing';
    },

    /**
     * Start the device TTS session. Checks TTS availability first.
     */
    async start(_token: string, _region: string, _voiceName: string): Promise<void> {
      if (disposed) return;
      if (status !== 'idle') {
        log.warn('start() called in non-idle state', { status });
        return;
      }

      setStatus('starting');

      try {
        const voices = await Speech.getAvailableVoicesAsync();
        if (disposed) return;
        if (voices.length === 0) {
          throw new Error('No TTS voices available on this device');
        }
      } catch (error) {
        if (disposed) return;
        setStatus('error');
        const err = error instanceof Error ? error : new Error(String(error));
        log.error('Device TTS not available', err);
        callbacks.onError(err);
        return;
      }

      chunker.reset();
      speakQueue = [];
      setStatus('active');
      log.info('Device streaming TTS session started');
    },

    /**
     * Feed a text token. Accumulates in SentenceChunker, speaks on sentence boundary.
     */
    feedToken(text: string): void {
      if (disposed || status !== 'active') return;
      const sentences = chunker.feed(text);
      for (const sentence of sentences) {
        log.debug('Device TTS sentence', { len: sentence.length, text: sentence.slice(0, 60) });
        speakSentence(sentence);
      }
    },

    /**
     * Signal that all text has been streamed. Speaks remaining text and waits for all audio.
     */
    async finish(): Promise<void> {
      if (disposed || (status !== 'active' && status !== 'starting')) return;

      setStatus('finishing');

      // Flush remaining buffered text
      const remaining = chunker.flush();
      if (remaining) {
        log.debug('Device TTS flush', { len: remaining.length, text: remaining.slice(0, 60) });
        speakSentence(remaining);
      }

      // Wait for all queued utterances to finish
      try {
        await Promise.all(speakQueue);
        if (disposed) return;
        setStatus('done');
        callbacks.onComplete();
      } catch (error) {
        if (disposed) return;
        setStatus('error');
        const err = error instanceof Error ? error : new Error(String(error));
        log.error('Device streaming TTS failed', err);
        callbacks.onError(err);
      }
    },

    /**
     * Cancel the session immediately. Stops audio playback.
     */
    cancel(): void {
      if (disposed) return;
      disposed = true;
      chunker.reset();
      try { Speech.stop(); } catch { /* no-op — device may lack TTS engine */ }
      setStatus('canceled');
    },

    /**
     * Dispose the session. Same as cancel but idempotent.
     */
    dispose(): void {
      if (disposed) return;
      this.cancel();
    },
  };
}

export interface ServerStreamingTTSOptions {
  /** Course voice, sent as the A/B same-voice override in every POST body. */
  voice?: string;
  /** Message attribution for server-side cost metering. During auto-play this
   *  is the OPTIMISTIC local id (`ai-…`) — the server ignores non-UUID ids —
   *  so sessionId below is what actually attributes these segments. */
  messageId?: string;
  /** Chat-session attribution label for cost metering (ADR-026). */
  sessionId?: string;
}

/**
 * A fetched clip paired with the per-segment latency metadata captured at
 * fetch time. Threaded from the fetch handler into playClip so the
 * TTS_SEGMENT log can report fetchMs (measured around the POST) and
 * playerInitMs (measured around playTtsClip) for the same segment.
 */
interface ReadySegment {
  clip: TtsClip;
  segmentIndex: number;
  chars: number;
  fetchMs: number;
}

/**
 * Create a server-proxied streaming TTS session (ttsProvider 'azure-server').
 *
 * The streamed reply is segmented into sentences (TtsSegmenter) and each
 * segment is synthesized via POST /api/speech/tts, played sequentially with
 * prefetch depth 1: while segment N plays, segment N+1 is being fetched (or
 * fetched and waiting) — never N+2. No Azure credential ever reaches this
 * path, so start() ignores token/region/voiceName (kept for interface parity
 * with the other session factories).
 *
 * Fail-fast like the device session: any fetch or playback error tears the
 * session down ('error' + onError) — the store owns the device-TTS fallback.
 *
 * Spec: docs/specs/mobile/server-autoplay-tts.mobile.md (ATTS-003)
 */
export function createServerStreamingTTSSession(
  callbacks: StreamingTTSCallbacks,
  options: ServerStreamingTTSOptions = {},
) {
  let status: StreamingTTSStatus = 'idle';
  let disposed = false;
  const segmenter = new TtsSegmenter();
  /** Segment texts awaiting fetch (FIFO). */
  const segments: string[] = [];
  /** Abort handle for the single in-flight fetch (prefetch depth 1). */
  let inFlight: AbortController | null = null;
  /** Fetched segment (clip + latency metadata) waiting for the current playback to end. */
  let readyClip: ReadySegment | null = null;
  /** The clip currently playing. */
  let playback: TtsPlayback | null = null;
  /** finish() was called and the segmenter remainder is queued. */
  let textDone = false;
  let firstAudioFired = false;
  /** 0-based index assigned to each segment when it is dequeued for fetch (reading order). */
  let nextSegmentIndex = 0;
  /** Resolvers for finish() awaiters (drain / error / cancel). */
  let finishResolve: (() => void) | null = null;
  /**
   * "No forward progress" watchdog. Restarted on every real progress event
   * (a fetch starts/resolves, a segment starts playing, a playback completes);
   * fires only when the window elapses while work is still pending. Catches
   * freezes the per-segment fetch timeout can't — e.g. a playback `done` that
   * never settles.
   */
  let watchdog: ReturnType<typeof setTimeout> | null = null;

  function setStatus(newStatus: StreamingTTSStatus) {
    status = newStatus;
    callbacks.onStatusChange(newStatus);
  }

  /** True while any segment is queued, fetching, fetched-and-waiting, or playing. */
  function workPending(): boolean {
    return inFlight !== null || readyClip !== null || playback !== null || segments.length > 0;
  }

  function clearWatchdog() {
    if (watchdog !== null) {
      clearTimeout(watchdog);
      watchdog = null;
    }
  }

  /** Reset the countdown on real progress; leave it cleared when idle/done. */
  function kickWatchdog() {
    clearWatchdog();
    if (disposed || !workPending()) return;
    watchdog = setTimeout(onWatchdogFired, TTS_SESSION_WATCHDOG_MS);
    // Node (test env) keeps the event loop alive for a pending timer; unref so a
    // still-armed watchdog can't block process exit. No-op on React Native,
    // whose timer handle is a plain number.
    (watchdog as unknown as { unref?: () => void }).unref?.();
  }

  function onWatchdogFired() {
    watchdog = null;
    if (disposed || !workPending()) return;
    log.warn('TTS_STALL_RECOVERED', {
      messageId: options.messageId ?? null,
      segmentIndex: nextSegmentIndex,
      reason: 'session_stalled',
    });
    fail(new Error('TTS session stalled'));
  }

  function resolveFinish() {
    if (finishResolve) {
      const resolve = finishResolve;
      finishResolve = null;
      resolve();
    }
  }

  function teardown() {
    clearWatchdog();
    const controller = inFlight;
    inFlight = null;
    controller?.abort();
    if (readyClip) {
      readyClip.clip.release();
      readyClip = null;
    }
    const active = playback;
    playback = null;
    active?.stop();
    segments.length = 0;
    segmenter.reset();
  }

  function fail(error: Error) {
    if (disposed) return;
    teardown();
    setStatus('error');
    disposed = true;
    log.error('Server streaming TTS failed', error);
    callbacks.onError(error);
    resolveFinish();
  }

  /** Start the next fetch when the prefetch slot is free. */
  function maybeFetchNext() {
    if (disposed || inFlight || readyClip || segments.length === 0) return;
    const text = segments.shift()!;
    const segmentIndex = nextSegmentIndex++;
    const chars = text.length;
    // fetchMs: wall-clock of the POST round-trip (backend synth + transfer)
    // for this segment (TTS_SEGMENT A/B instrument).
    const fetchStartedAt = Date.now();
    const controller = new AbortController();
    inFlight = controller;
    // A fetch just started — progress. Arm the watchdog so a hung request
    // (that somehow outlives even the per-segment fetch timeout) still recovers.
    kickWatchdog();
    fetchTtsAudio(text, {
      voice: options.voice,
      messageId: options.messageId,
      sessionId: options.sessionId,
      signal: controller.signal,
    }).then(
      (clip) => {
        if (disposed || inFlight !== controller) {
          // Session canceled/failed while the request was in flight
          clip.release();
          return;
        }
        inFlight = null;
        kickWatchdog(); // fetch resolved — progress
        const segment: ReadySegment = {
          clip,
          segmentIndex,
          chars,
          fetchMs: Date.now() - fetchStartedAt,
        };
        if (playback) {
          readyClip = segment;
        } else {
          playClip(segment);
          maybeFetchNext();
        }
      },
      (error: unknown) => {
        if (disposed || inFlight !== controller) return; // aborted by cancel()
        inFlight = null;
        const err = error instanceof Error ? error : new Error(String(error));
        // A per-segment fetch timeout (TtsFetchTimeoutError) is the recoverable
        // stall the store's device fallback exists for — surface it distinctly.
        if (err.name === 'TtsFetchTimeoutError') {
          log.warn('TTS_STALL_RECOVERED', {
            messageId: options.messageId ?? null,
            segmentIndex,
            reason: 'fetch_timeout',
          });
        }
        fail(err);
      },
    );
  }

  function playClip(segment: ReadySegment) {
    const { clip } = segment;
    if (disposed) {
      clip.release();
      return;
    }
    let current: TtsPlayback;
    // playerInitMs: wall-clock of the synchronous playTtsClip() (player
    // construction + first play() dispatch). The true first-audible frame is
    // not observable across the expo-audio bridge — play() returns before
    // audio starts — so this is a lower bound on device decoder/player init.
    const playStartedAt = Date.now();
    try {
      // onProgress (every ~500ms while playing) resets the watchdog so a
      // long-but-healthy segment isn't mistaken for a wedged playback.
      current = playTtsClip(clip, kickWatchdog);
    } catch (error) {
      // playTtsClip/createAudioPlayer can throw synchronously (bad URI,
      // native player exhaustion). playClip runs inside promise handlers,
      // so an uncaught throw would become an unhandled rejection and leave
      // a pending finish() hanging — route it to fail() instead.
      clip.release();
      fail(error instanceof Error ? error : new Error(String(error)));
      return;
    }
    // Per-segment latency decomposition for the server-path A/B (PII-free —
    // never the segment text). messageId is threaded to POST /speech/tts;
    // null when the caller supplied none.
    log.info('TTS_SEGMENT', {
      provider: 'azure-server',
      surface: 'autoplay',
      messageId: options.messageId ?? null,
      segmentIndex: segment.segmentIndex,
      chars: segment.chars,
      fetchMs: segment.fetchMs,
      playerInitMs: Date.now() - playStartedAt,
    });
    playback = current;
    kickWatchdog(); // a segment started playing — progress
    if (!firstAudioFired) {
      firstAudioFired = true;
      callbacks.onFirstAudio?.();
    }
    current.done.then((result) => {
      if (disposed || playback !== current) return;
      playback = null;
      if (result.error != null) {
        fail(new Error(`Server TTS playback failed: ${result.error}`));
        return;
      }
      kickWatchdog(); // a playback completed — progress
      if (readyClip) {
        const next = readyClip;
        readyClip = null;
        playClip(next);
      }
      maybeFetchNext();
      maybeComplete();
    });
  }

  function maybeComplete() {
    if (disposed || !textDone) return;
    if (segments.length > 0 || inFlight || readyClip || playback) return;
    clearWatchdog(); // fully drained — nothing left to watch
    setStatus('done');
    callbacks.onComplete();
    resolveFinish();
  }

  return {
    get status() {
      return status;
    },

    get isActive() {
      return status === 'active' || status === 'starting' || status === 'finishing';
    },

    /**
     * Start the session. Credentials are ignored — synthesis is server-side.
     */
    async start(_token: string, _region: string, _voiceName: string): Promise<void> {
      if (disposed) return;
      if (status !== 'idle') {
        log.warn('start() called in non-idle state', { status });
        return;
      }
      setStatus('starting');
      await configureTtsAudioMode();
      if (disposed) return;
      // finish() may have arrived while still 'starting' (short replies) —
      // the session is already 'finishing'; don't regress it to 'active'.
      if (!textDone) setStatus('active');
      log.info('Server streaming TTS session started', {
        platform: Platform.OS,
        hasVoiceOverride: options.voice != null,
      });
      // Tokens fed during 'starting' were already segmented — pump them.
      maybeFetchNext();
    },

    /**
     * Feed a text token from the conversation stream.
     */
    feedToken(text: string): void {
      if (disposed) return;
      if (status !== 'active' && status !== 'starting') return;
      const newSegments = segmenter.feed(text);
      if (newSegments.length > 0) {
        segments.push(...newSegments);
        // During 'starting' the pump waits for start() (audio mode setup).
        if (status === 'active') maybeFetchNext();
      }
    },

    /**
     * Signal that all text has been streamed. Flushes the segmenter
     * remainder and resolves when everything has been fetched and played
     * (or the session failed / was canceled — never hangs the store).
     */
    async finish(): Promise<void> {
      if (disposed || (status !== 'active' && status !== 'starting')) return;
      textDone = true;
      setStatus('finishing');
      segments.push(...segmenter.flush());
      maybeFetchNext();
      if (segments.length === 0 && !inFlight && !readyClip && !playback) {
        maybeComplete();
        return;
      }
      await new Promise<void>((resolve) => {
        finishResolve = resolve;
      });
    },

    /**
     * Cancel immediately: abort the in-flight fetch, stop playback,
     * drain the queue.
     */
    cancel(): void {
      if (disposed) return;
      disposed = true;
      teardown();
      status = 'canceled';
      callbacks.onStatusChange('canceled');
      resolveFinish();
    },

    /**
     * Dispose the session. Same as cancel but idempotent.
     */
    dispose(): void {
      if (disposed) return;
      this.cancel();
    },
  };
}

export type StreamingTTSSession = ReturnType<typeof createServerStreamingTTSSession>;
